import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Trash2, Clock, CheckCircle, XCircle, Info, TrendingDown, ImageIcon, Calendar, RefreshCw, AlertTriangle } from '../../icons';
import { formatDate } from '../../utils/dateUtils';
import { cn } from '../ui/cn';

const CHANGE_ICONS = {
  price: TrendingDown,
  photos: ImageIcon,
  date: Calendar,
  type: Info,
};

const SyncHistoryCard = ({ sync, onDelete }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatDuration = (seconds) => `${seconds}s`;

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const syncDate = new Date(timestamp);
    const diffInHours = (now - syncDate) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      const minutes = Math.floor(diffInHours * 60);
      return minutes === 0 ? 'Just now' : `${minutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return formatDate(syncDate);
    }
  };

  const hasDetails = (sync.changes && sync.changes.length > 0) || (sync.errors && sync.errors.length > 0);

  return (
    <article className="min-w-0 bg-card border border-line rounded-card shadow-card p-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          className="flex-1 min-w-0 flex items-center gap-3 text-left"
        >
          <div className="min-w-0">
            <div className="text-[15px] font-semibold text-primary">{formatTimestamp(sync.timestamp)}</div>
            <div className="text-xs text-secondary flex items-center gap-1.5">
              <Clock className="w-3 h-3" />
              {formatDuration(sync.duration)}
              <span className="text-muted">·</span>
              {formatDate(sync.timestamp)}
            </div>
          </div>
        </button>
        
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-secondary hover:text-primary hover:bg-white/5"
          >
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <button
            type="button"
            onClick={onDelete}
            aria-label="Delete sync record"
            className="w-9 h-9 flex items-center justify-center rounded-lg text-secondary hover:text-danger hover:bg-danger-soft"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Summary pills */}
      <div className="flex flex-wrap gap-1.5 mt-3">
        {sync.totalUpdated > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-accent-soft text-accent text-xs font-medium">
            <CheckCircle className="w-3 h-3" /> {sync.totalUpdated} updates
          </span>
        )}
        {sync.totalErrors > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-danger-soft text-danger text-xs font-medium">
            <XCircle className="w-3 h-3" /> {sync.totalErrors} errors
          </span>
        )}
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-page border border-line text-secondary text-xs">
          <Info className="w-3 h-3" /> {sync.totalChecked} checked
        </span>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="space-y-4 pt-3 mt-3 border-t border-line">
          {sync.changes && sync.changes.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-primary mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-accent" /> Updates ({sync.changes.length})
              </h4>
              <ul className="space-y-2">
                {sync.changes.map((change, index) => {
                  const Icon = CHANGE_ICONS[change.changeType] || RefreshCw;
                  return (
                    <li key={index} className="bg-page border border-line rounded-xl p-3 flex items-start gap-3">
                      <span className="w-8 h-8 rounded-lg bg-accent-soft text-accent flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-primary truncate">{change.propertyTitle}</div>
                        <div className="text-xs text-muted">{change.propertyReference}</div>
                        <div className="text-sm text-accent mt-0.5">{change.changeDescription}</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {sync.errors && sync.errors.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-primary mb-2 flex items-center gap-2">
                <XCircle className="w-4 h-4 text-danger" /> Errors ({sync.errors.length})
              </h4>
              <ul className="space-y-2">
                {sync.errors.map((error, index) => (
                  <li key={index} className="bg-page border border-line rounded-xl p-3 flex items-start gap-3">
                    <span className={cn('w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-danger-soft text-danger')}>
                      <AlertTriangle className="w-4 h-4" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-primary truncate">{error.propertyTitle}</div>
                      <div className="text-xs text-muted">{error.propertyReference}{error.errorType ? ` · ${error.errorType}` : ''}</div>
                      <div className="text-sm text-danger mt-0.5">{error.errorMessage}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!hasDetails && (
            <div className="text-center py-3 text-secondary">
              <Info className="w-6 h-6 mx-auto mb-1 opacity-60" />
              <div className="text-sm">No changes or errors recorded</div>
            </div>
          )}
        </div>
      )}
    </article>
  );
};

export default SyncHistoryCard;
